// /chord-craft/slither — the voice-leading room. Pick a progression, a key,
// and a color level; the loop is shown as a row of chord-symbol cards with
// the current + next chord highlighted. The piano always shows the current
// chord voiced with the previous chord in mind (voiceChord's `prev` option),
// so hands slither from shape to shape instead of jumping. "Play through"
// steps the loop on a timer, headlining the right hand's semitone movement
// (bass motion is a quiet aside) and glowing the guide tones (3rd/7th) that
// are doing the work, plus a small "what moved" readout naming which right-
// hand finger stayed and which hopped (voicingMoves' rightPairs).
// Drill mode freezes on one transition and grades how little the right hand
// moved getting to the next chord — same coaching tone as PianoDrills'
// pocket drill ("you moved N; best was M") — via matchesChord + voicingMoves,
// comparing the player's right-hand movement to the optimal voicing's.
//
// Audio is homeKeyAudio's softKeys patch (held notes via startNote for
// on-screen/MIDI play, fire-and-forget playNote for "hear it" / play-through
// chords, lightly staggered so a block chord doesn't sound like a click).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CraftShell from "../components/chordCraft/CraftShell";
import CraftPiano from "../components/chordCraft/CraftPiano";
import {
  PROGRESSIONS,
  VOICING_LEVELS,
  progressionChords,
  voiceChord,
  guideTones,
  voicingDistance,
  voicingMoves,
  matchesChord,
} from "../lib/chordCraft";
import { KEYS, keyByName, midiToName } from "../lib/pianoDrills";
import { PATCHES, startNote, playNote } from "../lib/homeKeyAudio";
import { useMidiInput } from "../components/synthlab/useSynthInput";
import { cn } from "../lib/utils";

const STORAGE_KEY = "chordCraft:slither:v1";

// Range the piano displays / accepts input over — matches voiceChord's own
// default lhLow/rhHigh so every level's voicing always fits on screen.
const LOW = 40;
const HIGH = 84;

const COLOR_LEVELS = [
  { id: "plain", label: "Plain" },
  { id: "warm", label: "Warm" },
  { id: "lush", label: "Lush" },
];

const PLAY_STEP_MS = 1700;

const loadPrefs = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const savePrefs = (prefs) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
};

const pcOf = (midi) => ((midi % 12) + 12) % 12;

// Build a { [midi]: 'left'|'right'|'color' } role map for CraftPiano from a
// voiceChord() result, optionally flagging the guide tones (3rd/7th) as
// 'color' so they visibly glow while they swap between chords.
const buildRoles = (voicing, spec, flagGuides) => {
  const roles = {};
  const gt = flagGuides && spec ? guideTones(spec) : null;
  const mark = (midis, role) => {
    (midis || []).forEach((m) => {
      const pc = pcOf(m);
      roles[m] = gt && (pc === gt.third || (gt.seventh != null && pc === gt.seventh))
        ? "color"
        : role;
    });
  };
  mark(voicing && voicing.left, "left");
  mark(voicing && voicing.right, "right");
  return roles;
};

// Rough hand split for grading a freely-played chord against a target
// voicing shape — everything below middle C counts as the left hand.
const splitPlayed = (midis) => ({
  left: midis.filter((m) => m < 60).sort((a, b) => a - b),
  right: midis.filter((m) => m >= 60).sort((a, b) => a - b),
});

// Quiet "what moved" line: the right hand's note-by-note story from
// voicingMoves().rightPairs — a stayed finger (the win) glows emerald,
// everything else just names the hop.
const RightHandStory = ({ moves, useFlats }) => {
  if (!moves || !moves.rightPairs.length) return null;
  return (
    <div className="text-sm text-stone-400">
      {moves.rightPairs.map((p, i) => (
        <span key={`${p.from}-${p.to}-${i}`}>
          {i > 0 && <span className="text-stone-600"> · </span>}
          {p.from === p.to ? (
            <span className="text-emerald-300">{midiToName(p.to, useFlats)} stayed</span>
          ) : (
            <span>
              {midiToName(p.from, useFlats)}→{midiToName(p.to, useFlats)}
            </span>
          )}
        </span>
      ))}
    </div>
  );
};

// Fire-and-forget block-chord playback, notes lightly staggered like a
// strum so it doesn't sound like one clicky attack.
const playChord = (voicing, whenMs = 0) => {
  const midis = [...(voicing.left || []), ...(voicing.right || [])];
  midis.forEach((m, i) => playNote(PATCHES.softKeys, m, 950, whenMs + i * 14));
};

export default function ChordCraftSlither() {
  const prefs = useMemo(loadPrefs, []);

  const [progId, setProgId] = useState(prefs.progId || (PROGRESSIONS[0] && PROGRESSIONS[0].id));
  const [keyName, setKeyName] = useState(prefs.keyName || "C");
  const [colorLevel, setColorLevel] = useState(prefs.colorLevel || "warm");
  const [level, setLevel] = useState(prefs.level || 2);

  const [currentIdx, setCurrentIdx] = useState(0);
  const [mode, setMode] = useState("view"); // 'view' | 'play' | 'drill'

  const [activeMidis, setActiveMidis] = useState(() => new Set());

  const [drill, setDrill] = useState(null);
  // drill: { fromIdx, fromVoicing, targetIdx, targetSpec, hint, result }
  const [drillStats, setDrillStats] = useState(() => ({
    attempts: prefs.attempts || 0,
    hits: prefs.hits || 0,
  }));

  useEffect(() => {
    savePrefs({ progId, keyName, colorLevel, level, ...drillStats });
  }, [progId, keyName, colorLevel, level, drillStats]);

  const prog = useMemo(
    () => PROGRESSIONS.find((p) => p.id === progId) || PROGRESSIONS[0],
    [progId]
  );
  const useFlats = Boolean(keyByName(keyName) && keyByName(keyName).useFlats);

  const steps = useMemo(
    () => (prog ? progressionChords(prog, keyName, colorLevel) : []),
    [prog, keyName, colorLevel]
  );

  // Changing progression/key/color resets the walk through the loop and
  // drops out of play/drill mode so nothing is grading a stale chord.
  useEffect(() => {
    setCurrentIdx(0);
    setMode("view");
    setDrill(null);
  }, [progId, keyName, colorLevel]);

  /* ---------- audio + input plumbing ---------- */

  const voicesRef = useRef({});

  const press = useCallback((midi) => {
    if (voicesRef.current[midi]) return;
    voicesRef.current[midi] = startNote(PATCHES.softKeys, midi);
    setActiveMidis((s) => new Set(s).add(midi));
  }, []);

  const release = useCallback((midi) => {
    const v = voicesRef.current[midi];
    if (v) {
      v.stop();
      delete voicesRef.current[midi];
    }
    setActiveMidis((s) => {
      if (!s.has(midi)) return s;
      const n = new Set(s);
      n.delete(midi);
      return n;
    });
  }, []);

  useEffect(() => {
    const held = voicesRef.current;
    return () => {
      Object.values(held).forEach((v) => v.stop());
      Object.keys(held).forEach((k) => delete held[k]);
    };
  }, []);

  useMidiInput(press, release, true);

  /* ---------- current chord + voice leading ---------- */

  const prevVoicingRef = useRef(null);

  const { voicing: currentVoicing, moves } = useMemo(() => {
    const step = steps[currentIdx];
    if (!step) return { voicing: { left: [], right: [] }, moves: null };
    const prev = mode === "drill" ? null : prevVoicingRef.current;
    const voicing = voiceChord(step.spec, level, { prev });
    return { voicing, moves: prev ? voicingMoves(prev, voicing) : null };
  }, [steps, currentIdx, level, mode]);

  useEffect(() => {
    if (mode !== "drill") prevVoicingRef.current = currentVoicing;
  }, [currentVoicing, mode]);

  // Reset voice-leading memory whenever the level changes shape (a new
  // level's ranges make the old "previous" voicing meaningless).
  useEffect(() => {
    prevVoicingRef.current = null;
  }, [level]);

  const step = steps[currentIdx] || null;
  const nextIdx = steps.length ? (currentIdx + 1) % steps.length : 0;

  const goTo = useCallback(
    (idx) => {
      if (mode === "drill") return;
      setCurrentIdx(((idx % steps.length) + steps.length) % steps.length);
    },
    [mode, steps.length]
  );

  /* ---------- play-through ---------- */

  useEffect(() => {
    if (mode !== "play" || !steps.length) return undefined;
    playChord(currentVoicing);
    const id = setInterval(() => {
      setCurrentIdx((i) => (i + 1) % steps.length);
    }, PLAY_STEP_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, steps.length, currentIdx]);

  const togglePlay = () => {
    setDrill(null);
    setMode((m) => (m === "play" ? "view" : "play"));
  };

  /* ---------- drill mode ---------- */

  const startDrill = () => {
    if (!steps.length) return;
    setMode("drill");
    setDrill({
      fromIdx: currentIdx,
      fromVoicing: currentVoicing,
      targetIdx: nextIdx,
      targetSpec: (steps[nextIdx] || {}).spec,
      hint: false,
      result: null,
    });
  };

  const exitDrill = () => {
    setMode("view");
    setDrill(null);
    prevVoicingRef.current = currentVoicing;
  };

  const drillOptimal = useMemo(() => {
    if (!drill) return null;
    const voicing = voiceChord(drill.targetSpec, level, { prev: drill.fromVoicing });
    return {
      voicing,
      distance: voicingDistance(drill.fromVoicing, voicing),
      moves: voicingMoves(drill.fromVoicing, voicing),
    };
  }, [drill, level]);

  const advanceDrill = useCallback(() => {
    setDrill((d) => {
      if (!d || !drillOptimal) return d;
      const fromIdx = d.targetIdx;
      const fromVoicing = drillOptimal.voicing;
      const targetIdx = (fromIdx + 1) % steps.length;
      return {
        fromIdx,
        fromVoicing,
        targetIdx,
        targetSpec: (steps[targetIdx] || {}).spec,
        hint: false,
        result: null,
      };
    });
  }, [drillOptimal, steps]);

  // Grade whatever's currently held whenever it changes in drill mode.
  useEffect(() => {
    if (mode !== "drill" || !drill || !drillOptimal || (drill.result && drill.result.status === "success")) {
      return;
    }
    const midis = [...activeMidis];
    const pcs = new Set(midis.map(pcOf));
    if (pcs.size < 3) {
      if (drill.result) setDrill((d) => (d ? { ...d, result: null } : d));
      return;
    }
    const ok = matchesChord(midis, drill.targetSpec);
    if (ok) {
      const played = splitPlayed(midis);
      const moved = voicingMoves(drill.fromVoicing, played);
      setDrillStats((s) => ({ attempts: s.attempts + 1, hits: s.hits + 1 }));
      setDrill((d) => ({ ...d, result: { status: "success", moved, optimal: drillOptimal.moves } }));
    } else {
      setDrill((d) => (d.result && d.result.status === "wrong" ? d : { ...d, result: { status: "wrong" } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMidis, mode]);

  // Auto-advance to the next transition a beat after a successful drill.
  useEffect(() => {
    if (mode !== "drill" || !drill || !drill.result || drill.result.status !== "success") return undefined;
    const id = setTimeout(advanceDrill, 1500);
    return () => clearTimeout(id);
  }, [mode, drill, advanceDrill]);

  const revealHint = () => setDrill((d) => (d ? { ...d, hint: true } : d));

  /* ---------- render ---------- */

  const displayVoicing = mode === "drill" ? drill && drill.fromVoicing : currentVoicing;
  const displaySpec = mode === "drill" ? drill && drill.fromVoicing && (steps[drill.fromIdx] || {}).spec : step && step.spec;
  const roles = mode === "drill"
    ? drill && drill.hint
      ? buildRoles(drillOptimal ? drillOptimal.voicing : { left: [], right: [] }, drill.targetSpec, true)
      : buildRoles(displayVoicing || { left: [], right: [] }, displaySpec, false)
    : buildRoles(currentVoicing, step && step.spec, mode === "play");

  const accuracy = drillStats.attempts > 0 ? Math.round((100 * drillStats.hits) / drillStats.attempts) : null;

  return (
    <CraftShell room="slither" title="Slither" subtitle="voice leading — tiny moves, smooth sound">
      {/* Controls */}
      <section className="mb-6 space-y-4">
        <div>
          <div className="mb-1.5 text-xs uppercase tracking-wide text-stone-500">Progression</div>
          <div className="flex flex-wrap gap-2">
            {PROGRESSIONS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setProgId(p.id)}
                className={cn(
                  "rounded-full px-3 py-1.5 text-sm transition-colors",
                  p.id === progId
                    ? "bg-emerald-400 text-stone-950 font-medium"
                    : "bg-stone-800 text-stone-300 hover:bg-stone-700"
                )}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-6">
          <div>
            <div className="mb-1.5 text-xs uppercase tracking-wide text-stone-500">Key</div>
            <div className="flex flex-wrap gap-1.5">
              {KEYS.map((k) => (
                <button
                  key={k.name}
                  type="button"
                  onClick={() => setKeyName(k.name)}
                  className={cn(
                    "rounded-full px-2.5 py-1 text-xs transition-colors",
                    k.name === keyName
                      ? "bg-emerald-400 text-stone-950 font-medium"
                      : "bg-stone-800 text-stone-300 hover:bg-stone-700"
                  )}
                >
                  {k.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-1.5 text-xs uppercase tracking-wide text-stone-500">Color</div>
            <div className="flex gap-1.5">
              {COLOR_LEVELS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setColorLevel(c.id)}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs transition-colors",
                    c.id === colorLevel
                      ? "bg-emerald-400 text-stone-950 font-medium"
                      : "bg-stone-800 text-stone-300 hover:bg-stone-700"
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-stone-600">
              plain = bare triads · warm = sevenths on every chord · lush = sevenths + 9s
            </p>
          </div>

          <div>
            <div className="mb-1.5 text-xs uppercase tracking-wide text-stone-500">Voicing level</div>
            <div className="flex gap-1.5">
              {VOICING_LEVELS.map((lv) => (
                <button
                  key={lv.id}
                  type="button"
                  title={lv.blurb}
                  onClick={() => setLevel(lv.id)}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs transition-colors",
                    lv.id === level
                      ? "bg-emerald-400 text-stone-950 font-medium"
                      : "bg-stone-800 text-stone-300 hover:bg-stone-700"
                  )}
                >
                  {lv.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Symbol cards */}
      <section className="mb-6 flex flex-wrap gap-2">
        {steps.map((s, i) => {
          const isCurrent = mode === "drill" ? i === drill?.fromIdx : i === currentIdx;
          const isNext = mode === "drill" ? i === drill?.targetIdx : i === nextIdx;
          return (
            <button
              key={`${s.roman}-${i}`}
              type="button"
              disabled={mode !== "view"}
              onClick={() => goTo(i)}
              className={cn(
                "rounded-xl border px-3 py-2 text-left transition-colors",
                isCurrent
                  ? "border-emerald-400 bg-emerald-400/10"
                  : isNext
                  ? "border-emerald-400/50 border-dashed bg-stone-900/80"
                  : "border-stone-800 bg-stone-900/80",
                mode === "view" && "hover:border-emerald-400/60"
              )}
            >
              <div className="text-[10px] uppercase tracking-wide text-stone-500">{s.roman}</div>
              <div className="font-serif text-lg text-amber-50">{s.symbol}</div>
            </button>
          );
        })}
      </section>

      {/* Piano */}
      <section className="mb-6 rounded-2xl border border-stone-800 bg-stone-900/80 p-4">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <div className="font-serif text-2xl text-amber-50">
              {mode === "drill"
                ? (steps[drill?.fromIdx] || {}).symbol
                : step && step.symbol}
            </div>
            {mode === "play" && (
              <div className="text-sm text-stone-400">
                {moves == null ? (
                  "start of the loop"
                ) : (
                  <>
                    right hand moved {moves.right} semitone{moves.right === 1 ? "" : "s"}
                    <span className="ml-2 text-xs text-stone-600">bass walked {moves.left}</span>
                  </>
                )}
              </div>
            )}
            {mode === "play" && <RightHandStory moves={moves} useFlats={useFlats} />}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => playChord(mode === "drill" ? drill?.fromVoicing || { left: [], right: [] } : currentVoicing)}
              className="rounded-full bg-stone-800 px-3 py-1.5 text-sm text-stone-300 hover:bg-stone-700"
            >
              hear it
            </button>
            <button
              type="button"
              onClick={togglePlay}
              className={cn(
                "rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                mode === "play" ? "bg-emerald-400 text-stone-950" : "bg-stone-800 text-stone-300 hover:bg-stone-700"
              )}
            >
              {mode === "play" ? "stop" : "play through"}
            </button>
            {mode !== "drill" ? (
              <button
                type="button"
                onClick={startDrill}
                className="rounded-full bg-stone-800 px-3 py-1.5 text-sm text-stone-300 hover:bg-stone-700"
              >
                drill this transition
              </button>
            ) : (
              <button
                type="button"
                onClick={exitDrill}
                className="rounded-full bg-stone-800 px-3 py-1.5 text-sm text-stone-300 hover:bg-stone-700"
              >
                exit drill
              </button>
            )}
          </div>
        </div>

        <CraftPiano
          low={LOW}
          high={HIGH}
          roles={roles}
          activeMidis={activeMidis}
          onPress={press}
          onRelease={release}
          useFlats={useFlats}
          showLabels
        />
      </section>

      {/* Drill panel */}
      {mode === "drill" && drill && (
        <section className="rounded-2xl border border-emerald-400/40 bg-stone-900/80 p-4">
          <div className="mb-2 flex flex-wrap items-center gap-2 font-serif text-lg text-amber-50">
            <span>{(steps[drill.fromIdx] || {}).symbol}</span>
            <span className="text-emerald-400">→</span>
            <span>{(steps[drill.targetIdx] || {}).symbol}</span>
          </div>
          <p className="mb-3 text-sm text-stone-400">
            Get to the next chord moving as little as you can — then hold it.
          </p>

          {drill.result && drill.result.status === "success" && (
            <div className="mb-3 rounded-lg border border-emerald-400/50 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-300">
              <div>
                {drill.result.moved.right <= drill.result.optimal.right
                  ? `right hand moved ${drill.result.moved.right} — smoothest was ${drill.result.optimal.right}. nice.`
                  : `right hand moved ${drill.result.moved.right} — smoothest was ${drill.result.optimal.right}`}
                <span className="ml-2 text-xs text-emerald-300/60">bass moved {drill.result.moved.left}</span>
              </div>
              <RightHandStory moves={drill.result.moved} useFlats={useFlats} />
            </div>
          )}
          {drill.result && drill.result.status === "wrong" && (
            <div className="mb-3 rounded-lg border border-stone-700 bg-stone-800/60 px-3 py-2 text-sm text-stone-400">
              not quite that chord yet — keep listening for it.
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={revealHint}
              className="rounded-full bg-stone-800 px-3 py-1.5 text-sm text-stone-300 hover:bg-stone-700"
            >
              hint
            </button>
            <button
              type="button"
              onClick={advanceDrill}
              className="rounded-full bg-stone-800 px-3 py-1.5 text-sm text-stone-300 hover:bg-stone-700"
            >
              skip to next transition
            </button>
          </div>

          <div className="mt-3 text-xs text-stone-500">
            {drillStats.attempts} attempt{drillStats.attempts === 1 ? "" : "s"}
            {accuracy != null && ` · ${accuracy}% matched`}
          </div>
        </section>
      )}
    </CraftShell>
  );
}
